import React, { useEffect, useState } from "react";
import { Box, RadioGroup, FormControlLabel, Radio, Typography, Grid, Button } from "@mui/material";

import PieChart from "../PieChart";
import { exportImage, PrivateNetworksValuesType } from "../../utils";
import { colors } from "../../constants";
import { Image } from "@mui/icons-material";
import { useGetVerticalsAsTreedataQuery } from "../../store/apis/search";

interface NetworksPieChartProps {
  data: any;
  type: string;
  setType: (numeric: string) => void;
  loading?: boolean;
  hideStatusOption?: boolean;
  showVerticalOption?: boolean;
  filters?: PrivateNetworksValuesType;
}

const NetworksPieChart: React.FC<NetworksPieChartProps> = ({
  data = [],
  type,
  setType,
  loading,
  hideStatusOption,
  showVerticalOption,
  filters,
}) => {
  const [numeric, setNumeric] = useState("total");
  const [total, setTotal] = useState(0);
  const [canvas, setCanvas] = useState<any>(null);

  const isVertical = type === "vertical";
  const { data: verticalData, isLoading: isVerticalLoading } = useGetVerticalsAsTreedataQuery(
    { values: filters! },
    { skip: !showVerticalOption || !filters },
  );
  const isLoading = isVertical ? isVerticalLoading : loading;

  useEffect(() => {
    if (data.length !== total) {
      setTotal(data.length);
    }
  }, [data, total]);

  const verticalItems = verticalData ?? [];
  const verticalTotal = verticalItems.reduce((sum, item) => sum + item.count, 0);

  const mappedData = isVertical
    ? verticalItems.map((item) => ({
      label: item.title,
      value: numeric === "total" ? item.count : verticalTotal > 0 ? Math.round((item.count / verticalTotal) * 10000) / 100 : 0,
    }))
    : data.map((item: any) => ({
      label: type === "chart_region" ? item.region_title : item.status_title,
      value: numeric === "total" ? item.numeric : item.percentage,
    }));

  return (
    <Box paddingY={4}>
      <Box marginY={16}>
        <RadioGroup
          name="type"
          value={type}
          row
          onChange={(_, value) => {
            setType(value);
          }}
        >
          <FormControlLabel value="chart_region" control={<Radio color="primary" />} label="Chart by region" />
          {!hideStatusOption && (
            <FormControlLabel value="chart_status" control={<Radio color="primary" />} label="Chart by status" />
          )}
          {showVerticalOption && (
            <FormControlLabel value="vertical" control={<Radio color="primary" />} label="Chart by vertical" />
          )}
        </RadioGroup>
      </Box>
      <Box marginY={16}>
        <Grid container justifyContent="space-between">
          <Grid>
            <Typography variant="h3">
              {isVertical ? "Chart Grouped by Vertical" : type === "chart_region" ? "Chart Grouped by Region" : "Chart Grouped by Status"}
            </Typography>
          </Grid>
          <Grid>
            <Button
              startIcon={<Image />}
              variant="outlined"
              onClick={() => {
                if (canvas) {
                  exportImage(canvas.canvas, `nts-pie-chart-${new Date().getTime()}`);
                }
              }}
            >
              Download PNG
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Box marginY={16}>
        <RadioGroup
          name="numeric"
          value={numeric}
          row
          onChange={event => {
            setNumeric(event.target.value);
          }}
        >
          <FormControlLabel disabled={isLoading} value="total" control={<Radio color="primary" />} label="Show as total" />
          <FormControlLabel disabled={isLoading} value="percent" control={<Radio color="primary" />} label="Show as percent" />
        </RadioGroup>
      </Box>
      {mappedData.length > 0 && (
        <PieChart loading={isLoading} data={mappedData} valueSuffix={numeric === "percent" ? "%" : ""} colors={colors} setRef={setCanvas} />
      )}
    </Box>
  );
};

export default NetworksPieChart;
